/**
 * Helpers for rewriting completed workout payloads stored on `organizations/.../reportExercise` docs.
 * Shape mirrors mighty45-mobile: `reportData.exercises[]` plus `type === "super_set"` groups.
 */

const CLIENT_ADDED_ACCENT_PURPLE = "#8B5CF6"; // Mirrors mobile `Colors.charts.purple` for non-plan adds

export { CLIENT_ADDED_ACCENT_PURPLE };

export function mapExerciseLeaves(reportData, mapLeaf) {
  if (!reportData?.exercises) return reportData;
  const exercises = reportData.exercises.map((ex) => {
    if (ex?.type === "super_set") {
      const sets = (ex.sets ?? []).map((set) => ({
        ...set,
        exercises: (set.exercises ?? []).map((nested) => mapLeaf(nested) ?? nested),
      }));
      return { ...ex, sets };
    }
    return mapLeaf(ex) ?? ex;
  });
  return { ...reportData, exercises };
}

export function exerciseMatchesId(exercise, candidateId) {
  if (!exercise || candidateId == null || candidateId === "") return false;
  return exercise.exercise === candidateId;
}

/** True if replacing id would alter this subtree. */
export function reportUsesExerciseId(reportData, candidateId) {
  if (!reportData?.exercises || !candidateId) return false;
  let hit = false;
  mapExerciseLeaves(reportData, (leaf) => {
    if (exerciseMatchesId(leaf, candidateId)) hit = true;
    return leaf;
  });
  return hit;
}

/**
 * Swap one catalog exercise id for another wherever it appears inside `reportData.exercises`.
 * @returns {{ reportData: object, changed: boolean }}
 */
export function replaceExerciseIdsInReportData(reportData, fromId, toId, toExerciseName) {
  let changed = false;
  const next = mapExerciseLeaves(reportData, (leaf) => {
    if (!exerciseMatchesId(leaf, fromId)) return leaf;
    changed = true;
    return {
      ...leaf,
      exercise: toId,
      ...(toExerciseName != null && toExerciseName !== ""
        ? { exerciseName: toExerciseName }
        : {}),
    };
  });
  return { reportData: next, changed };
}

export function collectOccurrencesMatchingExercise(reportData, candidateId) {
  /** @type {{ path: string, leaf: object }[]} */
  const out = [];
  if (!reportData?.exercises?.length || !candidateId) return out;

  reportData.exercises.forEach((ex, exIdx) => {
    const indexLabel = exIdx + 1;

    if (ex?.type === "super_set") {
      const ssName = ex.superSetName || `Super set ${indexLabel}`;
      (ex.sets ?? []).forEach((round, roundIdx) => {
        (round.exercises ?? []).forEach((nested, nestedIdx) => {
          if (!exerciseMatchesId(nested, candidateId)) return;
          out.push({
            path: `${ssName} · Round ${roundIdx + 1} · Exercise ${nestedIdx + 1}`,
            leaf: nested,
          });
        });
      });
      return;
    }

    if (!exerciseMatchesId(ex, candidateId)) return;

    const title = ex.exerciseName ?? ex.exercise ?? `Exercise ${indexLabel}`;
    out.push({
      path: `Exercise ${indexLabel}: ${title}`,
      leaf: ex,
    });
  });

  return out;
}

function pickSetWeight(set) {
  const raw = set?.workingWeight ?? set?.weight;
  if (raw === null || raw === undefined || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function pickSetRepsDisplay(set) {
  const cr = set?.completedReps;
  if (cr != null && cr !== "" && Number.isFinite(Number(cr))) {
    return { label: `${Math.round(Number(cr))} reps (logged)`, kind: "done" };
  }
  const r = set?.reps;
  if (r != null && r !== "" && Number.isFinite(Number(r))) {
    const target = Math.round(Number(r));
    if (set?.completed === true) return { label: `${target} reps (target)`, kind: "target" };
    return { label: `${target} reps (planned)`, kind: "planned" };
  }
  return { label: null, kind: "none" };
}

/**
 * Human-readable bullets for validating a merge: exercise-level skipped + each set.
 * @returns {string[]}
 */
export function summarizeRecordedSetsForMergeLeaf(leaf, weightUnit = "lbs") {
  const lines = [];
  if (!leaf) return lines;

  if (leaf.skipped === true) {
    lines.push("Whole exercise skipped");
  }

  const sets = Array.isArray(leaf.sets) ? leaf.sets : [];
  if (sets.length === 0) {
    if (lines.length === 0) lines.push("No set breakdown stored on this workout.");
    return lines;
  }

  sets.forEach((set, idx) => {
    const n = idx + 1;
    if (set?.skipped === true) {
      const w = pickSetWeight(set);
      const r = pickSetRepsDisplay(set);
      const wt = w == null ? "—" : `${w} ${weightUnit}`;
      const rep = r.label ? ` · ${r.label}` : "";
      lines.push(`Set ${n}: skipped · ${wt}${rep}`);
      return;
    }

    const w = pickSetWeight(set);
    const r = pickSetRepsDisplay(set);
    const wt = w == null ? "— wt" : `${w} ${weightUnit}`;
    const reps = r.label ?? "reps unknown";
    const status = set?.completed === true ? "" : " (set not marked complete)";
    lines.push(`Set ${n}: ${wt} · ${reps}${status}`);
  });

  return lines;
}

/**
 * Normalize values so Firestore does not choke on undefined (optional hygiene before writes).
 */
export function sanitizeForFirestore(value) {
  return JSON.parse(JSON.stringify(value ?? null));
}

